import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRightLeft } from 'lucide-react';
import { Button, Input, Modal } from '@/components/ui';
import type { AppDispatch, RootState } from '@/store';
import { transferResource } from '@/store/slices/resourceTeamAssignmentsSlice';
import type { ResourceTeamAssignment } from '@/types';

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: ResourceTeamAssignment | null;
}

const TransferModal: React.FC<TransferModalProps> = ({
  isOpen,
  onClose,
  assignment,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const teams = useSelector((state: RootState) => state.teams.teams) || [];
  const loading = useSelector((state: RootState) => state.resourceTeamAssignments.loading);

  const [newTeamId, setNewTeamId] = useState('');
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNewTeamId('');
      setTransferDate(new Date().toISOString().split('T')[0]);
      setError('');
    }
  }, [isOpen]);

  const handleTransfer = async () => {
    if (!assignment) return;

    if (!newTeamId) {
      setError('Please select a team');
      return;
    }

    if (newTeamId === assignment.teamId) {
      setError('Please select a different team');
      return;
    }

    if (!transferDate) {
      setError('Please select a transfer date');
      return;
    }

    const result = await dispatch(
      transferResource({
        resourceId: assignment.resourceId,
        newTeamId,
        transferDate: new Date(transferDate).toISOString(),
      })
    );

    if (transferResource.fulfilled.match(result)) {
      onClose();
    } else {
      setError('Failed to transfer resource. Please try again.');
    }
  };

  if (!assignment) return null;

  const currentTeam = teams.find(t => t.id === assignment.teamId);
  const availableTeams = teams.filter(t => t.id !== assignment.teamId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Transfer Resource"
      size="lg"
    >
      <div className="space-y-4">
        {/* Current Assignment Info */}
        <div className="bg-[var(--surface-muted)] border border-[var(--border)] p-4 rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-2">Current Assignment</h4>
          {assignment.resource && (
            <div className="mb-2">
              <span className="text-sm text-muted-foreground">Resource:</span>
              <span className="ml-2 text-sm font-medium text-foreground">
                {assignment.resource.firstName} {assignment.resource.lastName}
              </span>
              <span className="ml-1 text-xs text-muted-foreground font-mono">
                ({assignment.resource.resourceId})
              </span>
            </div>
          )}
          {currentTeam && (
            <div>
              <span className="text-sm text-muted-foreground">Current Team:</span>
              <span className="ml-2 text-sm font-medium text-foreground">
                {currentTeam.name}
              </span>
              {currentTeam.program && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({currentTeam.program.name})
                </span>
              )}
            </div>
          )}
        </div>

        {/* Transfer Form */}
        <div className="flex items-center justify-center py-2">
          <ArrowRightLeft className="w-6 h-6 text-primary-600" />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            New Team <span className="text-status-error">*</span>
          </label>
          <select
            className={`w-full px-3 py-2 border rounded-lg bg-[var(--surface)] text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] ${
              error && !newTeamId ? 'border-status-error' : 'border-[var(--border)]'
            }`}
            value={newTeamId}
            onChange={(e) => {
              setNewTeamId(e.target.value);
              setError('');
            }}
          >
            <option value="">Select new team...</option>
            {availableTeams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name} {team.program ? `(${team.program.name})` : ''}
              </option>
            ))}
          </select>
          {error && !newTeamId && (
            <p className="mt-1 text-xs text-status-error font-medium">{error}</p>
          )}
        </div>

        <Input
          type="date"
          label="Transfer Date"
          value={transferDate}
          onChange={(e) => {
            setTransferDate(e.target.value);
            setError('');
          }}
          required
        />

        {error && newTeamId && (
          <div className="bg-status-error/10 border border-status-error/30 text-status-error px-3 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Transfer Process:</strong>
          </p>
          <ul className="text-sm text-blue-800 mt-1 ml-4 space-y-1 list-disc">
            <li>Current assignment will end on the transfer date</li>
            <li>New assignment will start on the transfer date</li>
            <li>Assignment history will be preserved</li>
          </ul>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleTransfer}
            disabled={loading}
            leftIcon={<ArrowRightLeft className="w-4 h-4" />}
          >
            {loading ? 'Transferring...' : 'Transfer Resource'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TransferModal;
